<script setup lang="ts">
import { computed } from 'vue'

import { contentfulUrl } from '@internal/format-contentful'
import TeaserCardItemContent from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemContent.vue'
import TeaserCardItemMedia from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemMedia.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import { teaserCardItemVariants } from '@storefront-satch/components/molecules/teaser-card/variants'

const props = defineProps<TeaserCardItemProps & TeaserCardItemAdditionalProps>()

const hasVideo = computed(
  () =>
    !!(
      props.media?.videoMobile?.url ||
      props.media?.videoTablet?.url ||
      props.media?.videoDesktop?.url
    )
)
const videoHasControls = computed(() => props.media?.videoControls)
const videoIsAutoplay = computed(() => props.media?.videoAutoplay)
const hasLinkAndLinkText = computed(() => !!(props.link && props.linkText))

const width = computed(() => props.width ?? 'single')
const type = computed(() => props.type ?? 'half')

const isFullVideo = computed(() => {
  return hasVideo.value && type.value === 'full'
})

const showVideoWithLinkButton = computed(() => {
  if (
    isFullVideo.value &&
    videoIsAutoplay.value &&
    !videoHasControls.value &&
    hasLinkAndLinkText.value
  ) {
    return true
  }

  return type.value === 'half' && hasLinkAndLinkText.value
})

const isLinkElement = computed(() => {
  return props.link || showVideoWithLinkButton.value
})

const emit = defineEmits<{
  (e: 'select', id: string): void
}>()
</script>

<template>
  <div
    :id="props.sys.id"
    :class="
      teaserCardItemVariants({
        size: props.size,
        type,
        width,
        groupType: props.groupType,
      })
    "
    :data-cfid="props.sys.id"
    data-label="Teaser Card Item"
    :data-title="props.internalTitle"
    @click="emit('select', props.sys.id)"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      class="relative flex flex-wrap content-start p-0 hover:no-underline"
      :class="{
        'h-full': props.size === 'mini' && props.type === 'full',
      }"
      :href="isLinkElement ? contentfulUrl(props.link) : undefined"
      :data-gtm-attr="isLinkElement ? 'link_teaser-card-item-mini' : undefined"
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <TeaserCardItemMedia
        v-if="props.media"
        size="mini"
        :media="props.media"
        :type="type"
        :width="width"
        :group-type="props.groupType"
        :show-active="showActive"
        :media-aspect-ratio="props.mediaAspectRatio"
        :media-fit="props.mediaFit"
        :class="{
          'outline-primary outline outline-4': props.isActive && showActive,
        }"
      />

      <TeaserCardItemContent
        :is-clickable="hasLinkAndLinkText"
        :headline="props.content?.headline"
        content-alignment="center"
        :headline-size="showActive && isActive ? 'small' : 'extraSmall'"
        :headline-level="props.content?.headlineLevel || null"
        :content-style="
          props.contentStyle || (type === 'full' ? 'white' : 'black')
        "
        :class="[
          'm-0 flex w-full flex-wrap',
          {
            'absolute bottom-0 top-auto mb-5': type === 'full',
          },
        ]"
      />
    </component>
  </div>
</template>
